import React from 'react';
import { User } from 'lucide-react';
import { AddressLookup } from '../AddressLookup';

interface PersonalInfo {
  name: string;
  title: string;
  firstName: string;
  surname: string;
  gender: string;
  email: string;
  countryCode: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  region: string;
}

interface PersonalInfoStepProps {
  data: PersonalInfo;
  onInputChange: (field: string, value: string) => void;
  calculateAge: (dob: string) => number;
  user: any;
}

export function PersonalInfoStep({ data, onInputChange, calculateAge, user }: PersonalInfoStepProps) {
  // Clean phone number by removing any non-digit characters
  const cleanPhoneNumber = (phone: string) => {
    return phone ? phone.replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
  };

  React.useEffect(() => {
    if (user) {
      // Auto-fill email if user is logged in
      onInputChange('email', user.email || '');
      
      // Auto-fill name fields from metadata
      if (user.user_metadata) {
        if (user.user_metadata.firstName) {
          onInputChange('firstName', user.user_metadata.firstName);
        }
        if (user.user_metadata.surname) {
          onInputChange('surname', user.user_metadata.surname);
        }
      }

      // Get phone from user metadata with fallback to app metadata
      const userPhone = user.user_metadata?.phone || user.app_metadata?.phone || '';

      if (userPhone && userPhone.trim()) {
        // Extract country code and number
        const match = userPhone.match(/^(\+\d+)(.*)$/);
        if (match) {
          onInputChange('countryCode', match[1]);
          onInputChange('phone', cleanPhoneNumber(match[2]));
          console.log('Phone number set from metadata with country code:', {
            countryCode: match[1],
            phone: cleanPhoneNumber(match[2])
          });
        } else {
          // If no country code found, assume +44 and clean the whole number
          onInputChange('countryCode', '+44');
          onInputChange('phone', cleanPhoneNumber(userPhone));
          console.log('Phone number set from metadata without country code:', {
            countryCode: '+44',
            phone: cleanPhoneNumber(userPhone)
          });
        }
      }
    }
  }, [user]);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <User className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Personal Information</h2>
        {user && (
          <span className="text-sm text-gray-500 ml-2">
            (Some fields pre-filled from your account)
          </span>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Title
        </label>
        <select
          value={data.title || ''}
          onChange={(e) => onInputChange('title', e.target.value)}
          className="w-full p-2 border rounded"
          required
        >
          <option value="">Select Title</option>
          <option value="Mr">Mr</option>
          <option value="Mrs">Mrs</option>
          <option value="Miss">Miss</option>
          <option value="Ms">Ms</option>
          <option value="Dr">Dr</option>
          <option value="Rev">Rev</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="First Name"
          value={data.firstName || ''}
          onChange={(e) => onInputChange('firstName', e.target.value)}
          className="w-full p-2 border rounded"
          required
        />
        <input
          type="text"
          placeholder="Surname"
          value={data.surname || ''}
          onChange={(e) => onInputChange('surname', e.target.value)}
          className="w-full p-2 border rounded"
          required
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Gender
        </label>
        <select
          value={data.gender}
          onChange={(e) => onInputChange('gender', e.target.value)}
          className="w-full p-2 border rounded"
          required
        >
          <option value="">Select Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Region
        </label>
        <select
          value={data.region || ''}
          onChange={(e) => onInputChange('region', e.target.value)}
          className="w-full p-2 border rounded"
          required
        >
          <option value="">Select Region</option>
          <option value="england">England</option>
          <option value="scotland">Scotland</option>
          <option value="wales">Wales</option>
          <option value="northern_ireland">Northern Ireland</option>
        </select>
      </div>
      <input
        type="email"
        placeholder="Email Address"
        value={data.email}
        onChange={(e) => onInputChange('email', e.target.value)}
        readOnly={!!user}
        className="w-full p-2 border rounded"
        required
      />
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number
        </label>
        <div className="flex flex-wrap gap-2">
          <select
            value={data.countryCode}
            onChange={(e) => {
              onInputChange('countryCode', e.target.value);
              // Re-clean phone number when country code changes
              onInputChange('phone', cleanPhoneNumber(data.phone));
            }}
            className="w-20 md:w-24 p-2 border rounded"
            required
          >
            <option value="+44">+44</option>
            <option value="+353">+353</option>
            <option value="+1">+1</option>
            <option value="+33">+33</option>
            <option value="+49">+49</option>
          </select>
          <input
            type="tel"
            placeholder="Phone Number"
            value={data.phone}
            onChange={(e) => {
              const value = e.target.value;
              const cleanedNumber = cleanPhoneNumber(value);
              if (cleanedNumber !== data.phone) {
                onInputChange('phone', cleanedNumber);
              }
            }}
            className="flex-1 p-2 border rounded"
            pattern="[0-9]*"
            required
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Date of Birth
        </label>
        <div className="flex gap-2 items-start">
          <input
            type="date"
            value={data.dateOfBirth}
            onChange={(e) => onInputChange('dateOfBirth', e.target.value)}
            className="flex-1 p-2 border rounded"
            max={new Date(new Date().setFullYear(new Date().getFullYear() - 17)).toISOString().split('T')[0]}
            required
          />
          {data.dateOfBirth && (
            <div className="px-3 py-2 bg-gray-100 rounded border text-gray-700 text-sm font-medium min-w-[4rem] text-center">
              {calculateAge(data.dateOfBirth)} years
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Address
          </label>
          <AddressLookup
            onAddressSelect={(address) => onInputChange('address', address)}
          />
          <textarea
            placeholder="Selected address will appear here"
            className="w-full p-2 border rounded bg-gray-50"
            value={data.address}
            readOnly
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}
